<script setup lang="ts">
import { computed } from 'vue'
import { NButton, NSwitch, useMessage } from 'naive-ui'

// The kill switch for the public CV page. Publishing needs an approved
// version to show anything; the panel says so instead of hiding the toggle.
const props = defineProps<{ published: boolean; hasApproved: boolean; saving: boolean }>()
const emit = defineEmits<{ toggle: [published: boolean] }>()

const message = useMessage()

const publicUrl = computed(() => `${window.location.origin}/cv`)

async function copyUrl(): Promise<void> {
  try {
    await navigator.clipboard.writeText(publicUrl.value)
    message.success('Link copied')
  } catch {
    message.error('Could not copy the link')
  }
}
</script>

<template>
  <section class="rounded border border-white/10 bg-black/20 p-4 flex flex-col gap-3" data-testid="cv-publish">
    <div class="flex items-center justify-between gap-3">
      <div class="flex flex-col gap-0.5">
        <h2 class="text-lg font-semibold text-white m-0">Public page</h2>
        <p class="text-xs text-white/50 m-0">
          One stable URL, always the latest approved version. Off returns a plain 404.
        </p>
      </div>
      <NSwitch
        :value="props.published"
        :loading="props.saving"
        :disabled="props.saving"
        data-testid="cv-publish-toggle"
        @update:value="(v: boolean) => emit('toggle', v)"
      />
    </div>

    <p v-if="props.published && !props.hasApproved" class="text-xs text-amber-300 m-0" data-testid="cv-publish-warning">
      Published, but nothing is approved yet, so the page still shows 404. Approve a version to make it live.
    </p>

    <div v-if="props.published" class="flex flex-wrap items-center gap-2 text-sm" data-testid="cv-publish-url">
      <a :href="publicUrl" target="_blank" rel="noopener" class="text-blue-300 font-mono">{{ publicUrl }}</a>
      <NButton size="tiny" tertiary data-testid="cv-publish-copy" @click="copyUrl">Copy link</NButton>
    </div>
  </section>
</template>
